/**
 * Couor map engine.
 *
 * The map itself is finished artwork (assets/couor-map-world.svg) rather
 * than geometry generated at runtime, so this file is purely the camera
 * and the interaction layer: pan with momentum, zoom, fly-to-region, and
 * the markers that sit over the artwork.
 *
 * Coordinate system: the artwork's own viewBox, -4200..4200 on both axes,
 * with the rim at r=3620. SVG's y axis points down.
 *
 * How the camera stays smooth: writing the SVG viewBox re-rasterises
 * ~2,000 vector paths, so doing that per animation frame is a slideshow
 * on phones. Instead the camera is split in two:
 *
 *   camera  — where the viewer logically is (source of truth)
 *   painted — the viewBox actually written into the SVG
 *
 * The map is two stacked copies of the artwork:
 *
 *   base  — viewBox fixed at the whole world, FOREVER. It never repaints;
 *           every camera move is a pure compositor transform on it, so it
 *           is always geometrically correct (just soft when zoomed deep).
 *   sharp — sits on top and only ever changes viewBox while HIDDEN, then
 *           fades in once the browser has actually painted it.
 *
 * This exists because a viewBox write (slow main-thread re-raster) and a
 * CSS transform change (instant compositor) do not land atomically — doing
 * both in one breath shows the old raster under the new transform for a
 * few frames, hurling the view off the map. Split across two layers, no
 * frame can ever be wrong: motion shows base (correct, maybe soft), rest
 * shows sharp.
 */
class CouorMap extends HTMLElement {
  // Everything on the chart that moves under its own power. These live on
  // their own layer (see buildLifeLayer) — never baked into a bitmap, never
  // duplicated into the main artwork layers, and never suppressed.
  // What moves on its own, and therefore lives on the life layer.
  //
  // Only the sea monster qualifies. Two things learned the hard way:
  //
  //  • The rocking boats are `<image class="cre">`, authored for
  //    `mix-blend-mode: multiply` so white drops out. A transformed layer
  //    forms its own stacking context, which isolates the blend and turns
  //    every boat into a white rectangle.
  //  • The waves belong to `#cm-clearing-decor-under`, and the artwork
  //    paints the land tint OVER them. Lift them to a layer above
  //    everything and they stop being underneath anything — they cross
  //    coastlines and sit on top of land.
  //
  // Both are now static in the artwork instead: baked into the tiles and
  // present in the vector layers at the same phase, so they always render
  // in the right order and can never pop or ghost. `#cm-smseat` is listed
  // because the life script drives it directly and does not null-check it.
  static ANIMATED_SELECTOR =
    '#cm-smrise, #cm-smcalm, #cm-smchurn, #cm-smspout, #cm-smfroth, #cm-smseat';

  // The whole world, framed with a little air around the rim.
  // Wide enough that the whole disc sits inside the frame with sky around
  // it. The artwork's own extent is r=3620 (7240 across); at this span the
  // disc reads as an object in space rather than a floor filling the screen.
  static WORLD_VIEW = { x: -9000, y: -9000, w: 18000, h: 18000 };

  // The chart is a disc seen at an angle: squashed vertically when far off,
  // easing to flat-on as you come down to it. Update 3 ships this as a
  // transform INSIDE the svg; we drive it from the camera instead, so the
  // baked tiles stay tilt-free and remain valid at every zoom (a tile baked
  // at one squash is wrong at every other). Same curve as the kit's.
  // Where the artwork actually exists. The disc is 8400 across; the rim
  // cascade and its mist hang far below it, to y ~8300. Everything outside
  // this is sky, which the starfield draws — baking it wastes texture on
  // empty space, and that is exactly what left the map coarse: spread over
  // the 18000-unit sky view, the chart itself got barely a third of the
  // canvas and turned to mush the moment you zoomed toward it.
  static CONTENT_VIEW = { x: -4200, y: -4200, w: 8400, h: 12500 };

  static tiltFor(w) {
    const t = Math.min(1, Math.max(0, (w - 1400) / 7000));
    const e = t * t * (3 - 2 * t);
    return 1 - 0.55 * e;
  }

  // How wide a region reads when you travel to it.
  static REGION_VIEW_W = 890;

  // How long the travel tween runs. The menu reads this via flyMs() to
  // wait for arrival before opening the panel over the map.
  static FLY_DURATION = 900;

  // Phones get a shorter journey: same travel, less waiting.
  static flyMs() {
    return window.matchMedia('(max-width: 749px)').matches ? 550 : CouorMap.FLY_DURATION;
  }

  // The charted area as one high-resolution tile. Every menu destination
  // lives inside this square, so travel between them is covered by pixels
  // that are already sharp.
  // Sized to exactly contain both destinations' full region views (their
  // union spans ~1173 x 1274 map units) plus margin — no larger, because
  // every extra unit of span spends resolution the tile could be using.
  // Fallback only — the real span is derived per device in clearingViewBox().
  static CLEARING_SPAN = 1350;

  // The vector layers are drawn LARGER than the viewport by this margin.
  // A layer sized exactly to the viewport drags its own edge across the
  // screen whenever it translates during a pan, and that boundary — two
  // independently rasterised layers meeting — is visible as a flickering
  // seam. Travel between neighbouring regions moves ~260px on screen, so
  // this margin keeps every edge safely outside the visible area.
  // Desktop only: the extra raster area is not worth it on a phone, where
  // the clearing tile already exceeds screen resolution.
  static SHARP_MARGIN_PX = 300;

  static MIN_VIEW_W = 400;
  static MAX_VIEW_W = 18000;

  // Where a flown-to point lands on screen, as a fraction from the top.
  // Both aim just above the menu stack, which is dead-centred on every
  // device now -- 0.24 dated from the mobile menu's old raised anchor and
  // left arrivals sitting visibly high once that anchor was removed.
  // (Shrinking the pin fraction shrinks the coverage the clearing tile
  // needs, so every destination stays inside the prebaked view.)
  static PIN_TOP_FRACTION_MOBILE = 0.38;
  static PIN_TOP_FRACTION_DESKTOP = 0.38;

  connectedCallback() {
    // A static map is scenery: the camera never moves, so the drifting
    // weather and the creature rAF have nothing to contribute at this zoom
    // and are skipped. The vector layer STAYS — the falling rim water is
    // drawn in it, and it is the animation that makes the disc read as a
    // world rather than a picture of one.
    this.isStatic = this.hasAttribute('data-static');
    this.viewBox = { ...CouorMap.WORLD_VIEW };
    this.regions = Array.from(this.querySelectorAll('couor-map-region')).map((el) => ({
      el,
      name: el.dataset.name,
      anchor: el.dataset.anchor,
      state: el.dataset.state,
      pos: [parseFloat(el.dataset.x), parseFloat(el.dataset.y)],
    }));

    // The artwork is ~380KB and nothing can be drawn until it lands, so the
    // page kicks the fetch off inline during HTML parse rather than waiting
    // for this deferred script to parse and run (measured: 63ms of dead time
    // on a warm load, and far more on a cold mobile connection). Preload
    // links were the other option and were rejected: their cache-match rules
    // against a fetch() are subtle enough to risk downloading it twice.
    const startArt = () => {
      const started = window.__couorMapSvg && window.__couorMapSvg[this.dataset.svgUrl];
      const pending =
        started ||
        fetch(this.dataset.svgUrl).then((res) => {
          if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
          return res.text();
        });

      pending
        .then((svgText) => this.render(svgText))
        .catch((err) => console.error('[couor-map] failed to load map artwork', err));
    };

    // On content pages (collection/product/page) the map is the BACKDROP,
    // not the content: ad traffic lands there on phones, and the artwork
    // (~1.7MB with its textures) must not race the product photos for
    // cellular bandwidth. The old bootstrap swap got this ordering for free
    // by starting the whole map after the page's load event; the native
    // path recreates it here. The panel, menu and markers are server-side
    // and interactive regardless -- only the artwork waits. (map.liquid
    // also skips the inline parse-time fetch kick on these templates.)
    if (this.hasAttribute('data-defer-art')) {
      const idle = (fn) =>
        window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 2000 }) : setTimeout(fn, 200);
      if (document.readyState === 'complete') idle(startArt);
      else window.addEventListener('load', () => idle(startArt), { once: true });
    } else {
      startArt();
    }
  }

  render(svgText) {
    const holder = document.createElement('div');
    holder.innerHTML = svgText;
    const svg = holder.querySelector('svg');
    if (!svg) {
      console.error('[couor-map] artwork contained no <svg>');
      return;
    }

    this.absolutiseCreatures(svg);
    // Phones get half-resolution dressing textures. The fog decodes to
    // 16MB of RAM at 2048sq and the rim mist to 12MB -- at phone display
    // sizes the 1024 variants are indistinguishable and cost a quarter of
    // that. Swapped before the sharp clone and the mask inlining below, so
    // every copy (base, sharp, masks, raster clones) agrees.
    if (this.isMobile()) {
      const swaps = [
        ['couor-fog.webp', this.dataset.fogMobile],
        ['couor-rim-mist.webp', this.dataset.rimMobile],
      ];
      svg.querySelectorAll('image').forEach((img) => {
        const href = img.getAttribute('href') || '';
        for (const [file, url] of swaps) {
          if (url && href.split('?')[0].endsWith(file)) img.setAttribute('href', url);
        }
      });
    }
    this.inlineFallMask(svg);
    if (this.hasAttribute('data-defer-art')) this.deferDressing(svg);

    // Drop every wave squiggle outside the charted clearing. They are
    // invisible at world zoom, and they are the single biggest contributor
    // to path count — cheaper rasters everywhere for no visual loss.
    const worldWaves = Array.from(svg.querySelectorAll('.wav')).filter(
      (w) => !w.closest('#cm-clearing-decor-under')
    );
    worldWaves.forEach((w) => w.remove());
    this._culledWorldWaves = worldWaves.length;
    // "slice" so the artwork always covers the viewport (the kit ships
    // "meet", which would letterbox the map inside a full-bleed section).
    svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');

    const w = CouorMap.WORLD_VIEW;
    if (this.dataset.tileWorld) {
      // With prebaked tiles the base vector layer is pure load cost: it
      // exists only to be replaced by the world-tile canvas moments after
      // load, while the SHARP layer already paints the world. Cloning the
      // full artwork for it -- ~2,200 nodes and megabytes of data-URI
      // creature images, parsed a second time -- was the single biggest
      // main-thread bite of a phone load. A stub svg keeps every
      // replaceWith/transform code path intact at zero cost, and the
      // parsed artwork goes straight to the sharp layer.
      this.baseSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      this.baseSvg.classList.add('couor-map__svg', 'couor-map__svg--base');
      this.baseSvg.setAttribute('viewBox', `${w.x} ${w.y} ${w.w} ${w.h}`);
      this.sharpSvg = svg;
      this.sharpSvg.classList.add('couor-map__svg', 'couor-map__svg--sharp');
    } else {
      this.baseSvg = svg;
      this.baseSvg.classList.add('couor-map__svg', 'couor-map__svg--base');
      this.baseSvg.setAttribute('viewBox', `${w.x} ${w.y} ${w.w} ${w.h}`);

      this.sharpSvg = svg.cloneNode(true);
      this.sharpSvg.classList.remove('couor-map__svg--base');
      this.sharpSvg.classList.add('couor-map__svg--sharp');
    }

    // The sky must be the first painted thing in here. Ordering it in the
    // DOM rather than pushing it to z-index -1: a negative-z child paints
    // behind its PARENT'S background, and couor-map has an opaque one, so
    // -1 hid the starfield completely. First child at z-index 0 puts it
    // behind every map layer and still in front of that background.
    const sky = this.querySelector('#cm-space');
    if (sky) sky.after(this.baseSvg);
    else this.insertBefore(this.baseSvg, this.firstChild);
    this.baseSvg.after(this.sharpSvg);
    // Interactions bind to the host, not to a layer: the sharp layers swap
    // z-order as they alternate, so whichever is on top would otherwise
    // steal the pointer from the one holding the listeners.
    this.svgEl = this;

    // Sharp layers are double-buffered on desktop (see repaintSharpAt).
    // The second buffer is created lazily on the first repaint so it costs
    // nothing at load.
    this.sharpMargin = this.isMobile() ? 0 : CouorMap.SHARP_MARGIN_PX;
    this.style.setProperty('--couor-sharp-margin', `${this.sharpMargin}px`);

    this.sharpLayers = [this.sharpSvg];
    this.sharpViews = [this.paintedViewFor(this.viewBox)];
    // Per-layer geometry: width/height of the element and its origin
    // relative to the host. Null means "the CSS default" (viewport + margin,
    // inset by -margin), which is what the viewport-sized mode uses.
    this.sharpBox = [null];
    this.sharpFlags = [true];
    this.activeSharp = 0;

    this.camera = { ...this.viewBox };
    this.sharpPainted = { ...this.viewBox };
    this._sharpReady = true;
    const initialPaint = this.sharpViews[0];
    this.sharpSvg.setAttribute(
      'viewBox',
      `${initialPaint.x} ${initialPaint.y} ${initialPaint.w} ${initialPaint.h}`
    );
    this.applyCamera();
    this.placeRegions();
    this.bindInteraction();
    this.bindRegionEvents();
    this.classList.add('is-loaded');

    this.measureClearing();
    // The monster's own tiny layer. Losing this call is catastrophic in a
    // way that shows nothing on a fast machine: startLife() falls back to
    // driving the FULL sharp layer, whose ~3000px texture then re-rasters
    // on every one of the monster's ~20 attribute writes per frame.
    if (!this.isStatic) this.buildLifeLayer(this.sharpSvg);
    if (!this.isStatic) this.buildWeather();
    // Animated decor stays in the artwork layers, where the land paints over
    // the waves and the boats' multiply blend reaches the sea beneath. The
    // life script therefore drives whichever vector layer is on top, and is
    // re-pointed on every promotion (see repaintSharpAt).
    if (!this.isStatic) this.startLife();

    // The sea-monster loop rests while a product panel covers the map,
    // exactly like the CSS animations do (couor-map.css pauses those under
    // body.couor-product-open, but a rAF loop needs its own ear). Same
    // treatment as the flight pause: phase saved on cover, resumed
    // mid-cycle on reveal, so the monster never snaps.
    if (!this.isStatic && !this._panelWatch) {
      this._panelWatch = new MutationObserver(() => {
        const covered = document.body.classList.contains('couor-product-open');
        if (covered) {
          if (this._life) {
            this._lifePhase = this._life.phase ? this._life.phase() : 0;
            this._life.stop();
            this._lifeLayer = null;
          }
        } else if (!this.classList.contains('is-travelling')) {
          // startLife() itself declines while covered or travelling, so
          // this resume is safe to fire on any class change.
          this.startLife();
        }
      });
      this._panelWatch.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }

    // The base layer is painted at the whole world and gets scaled UP as
    // the camera zooms in — and a *vector* layer re-rasterises its ~2,000
    // paths every time the compositor decides the scale has drifted. That
    // is the single biggest cost in a zoom (measured: 20fps with a vector
    // base, 111fps without it). A one-time bitmap scales on the GPU for
    // free, so both platforms get one. The vector sharp layers still carry
    // all the crispness wherever they cover.
    //
    // Prebaked tiles start decoding immediately -- Image decode is async
    // and off the main thread, and the sooner the canvas replaces the
    // stub base the sooner flights have their backdrop. Only the live
    // bake (fallback, heavy, main-thread) still waits for idle.
    const kick = () => this.buildBitmapBase().catch(() => {});
    if (this.dataset.tileWorld) kick();
    else if (window.requestIdleCallback) requestIdleCallback(kick, { timeout: 1500 });
    else setTimeout(kick, 250);
  }

  async loadPrebakedTiles() {
    const mobile = this.isMobile();
    const worldUrl = (mobile && this.dataset.tileWorldMobile) || this.dataset.tileWorld;
    const clearUrl = (mobile && this.dataset.tileClearingMobile) || this.dataset.tileClearing;
    if (!worldUrl) throw new Error('no prebaked world tile');

    const load = (url) =>
      new Promise((res, rej) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => res(img);
        img.onerror = () => rej(new Error('tile failed: ' + url));
        img.src = url;
      });

    const worldImg = await load(worldUrl);
    if (worldImg.decode) await worldImg.decode().catch(() => {});
    // Blitted into a canvas so every downstream path (width/height reads,
    // replaceWith bookkeeping) sees exactly what the live bake produces.
    const canvas = document.createElement('canvas');
    canvas.width = worldImg.naturalWidth;
    canvas.height = worldImg.naturalHeight;
    canvas.getContext('2d').drawImage(worldImg, 0, 0);
    canvas.className = 'couor-map__world-tile';
    this.baseSvg.replaceWith(canvas);
    this.baseSvg = canvas;
    this.baseIsWorldTile = true;
    this.worldTileView = CouorMap.CONTENT_VIEW;
    this.applyCamera();

    if (clearUrl && this.dataset.tileClearingView) {
      const [x, y, w, h] = this.dataset.tileClearingView.split(/\s+/).map(Number);
      // The travel tile is only needed once a flight starts; its 16MB
      // decode has no business inside the load window. A short idle
      // defer keeps it well ahead of any human-speed first tap.
      await new Promise((res) => {
        if (window.requestIdleCallback) requestIdleCallback(res, { timeout: 2000 });
        else setTimeout(res, 800);
      });
      const img = await load(clearUrl);
      if (img.decode) await img.decode().catch(() => {});
      const tile = document.createElement('canvas');
      tile.width = img.naturalWidth;
      tile.height = img.naturalHeight;
      tile.getContext('2d').drawImage(img, 0, 0);
      tile.className = 'couor-map__clearing-tile';
      this.clearingView = { x, y, w, h };
      this.clearingTileEl = tile;
      this._clearingTileDone = true;
      this.baseSvg.after(tile);
      this.applyCamera();
    }
  }

  // Measure the charted clearing (the lit Bermuda Triangle area) from the
  // artwork. Nothing is drawn for it here — the fog itself is part of the
  // map artwork — but the bitmap bake needs the clearing's position to
  // reproduce the fog's clear zone, and it is the natural anchor for any
  // future weather layer.
  // The charted square, in map units. Both the high-resolution tile and
  // the drifting weather are anchored to exactly this box.
  // Is the charted tile, on its own, already at least as sharp as the
  // screen can resolve at the current camera — and does it cover the whole
  // view? When both hold, the vector layers add nothing: the tile IS the
  // render, and moving around costs a transform and nothing else. No
  // repaint, no reveal, nothing loading in.
  tileSufficient() {
    if (!this.clearingTileEl || !this.clearingView) return false;
    const v = this.clearingView;
    const rect = this.hostRect();
    if (!rect.width || !rect.height) return false;

    // Everything on screen must fall inside the tile.
    const pC = this.mapScale(rect, this.camera);
    const visW = rect.width / pC.scale;
    const visH = rect.height / pC.scale;
    const cx = this.camera.x + this.camera.w / 2;
    const cy = this.camera.y + this.camera.h / 2;
    const inside =
      cx - visW / 2 >= v.x && cx + visW / 2 <= v.x + v.w &&
      cy - visH / 2 >= v.y && cy + visH / 2 <= v.y + v.h;
    if (!inside) return false;

    // And it must not be magnified past what the display resolves. The 0.9
    // allows a hair of softness rather than paying for a repaint over a
    // difference nobody can see.
    const tileRes = this.clearingTileEl.width / v.w;
    const needRes = (rect.width / this.camera.w) * Math.min(devicePixelRatio, 2);
    return tileRes >= needRes * 0.9;
  }

  // Decide what carries the picture now that motion has stopped. Returns
  // true when the tile is doing it alone (so no repaint is required).
  // The tile and the vector layers both draw the decor, so exactly one of
  // them may ever be on screen — otherwise a frozen copy shows through a
  // live one and the two drift out of phase. Motion shows the tile; rest
  // shows the vector layer, which is the one that animates.
  syncTileVisibility() {
    if (!this.clearingTileEl) return;
    const vectorVisible = this.sharpLayers.some((l) => l.style.display !== 'none');
    this.clearingTileEl.style.display = vectorVisible ? 'none' : '';
  }

  // Kept for the resolution maths, but the tile no longer *replaces* the
  // vector layer at rest: only the vector layer animates, and a still map
  // with living water beats a marginally sharper frozen one.
  settleOnTile() {
    return false;
  }

  clearingViewBox() {
    if (!this.clearing) return null;

    // Derived, not guessed. The tile has to contain everything the screen
    // can see at every menu destination — so work out each destination's
    // visible rectangle exactly as flyTo() will frame it, union them, and
    // size the square to that. A hand-picked constant kept going stale
    // every time the zoom or the phone's aspect ratio changed, and a tile
    // that fails to cover is a tile the renderer cannot lean on.
    const rect = this.hostRect();
    const w = CouorMap.REGION_VIEW_W;
    if (rect.width && rect.height && this.regions && this.regions.length) {
      const scale = Math.max(rect.width / w, rect.height / w);
      const visW = rect.width / scale;
      const visH = rect.height / scale;
      const pin = this.isMobile()
        ? CouorMap.PIN_TOP_FRACTION_MOBILE
        : CouorMap.PIN_TOP_FRACTION_DESKTOP;

      let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
      let any = false;
      for (const r of this.regions) {
        const [mx, my] = r.pos;
        if (Number.isNaN(mx) || Number.isNaN(my)) continue;
        any = true;
        const cy = my + visH * (0.5 - pin); // flyTo's vertical bias
        x0 = Math.min(x0, mx - visW / 2);
        x1 = Math.max(x1, mx + visW / 2);
        y0 = Math.min(y0, cy - visH / 2);
        y1 = Math.max(y1, cy + visH / 2);
      }
      if (any) {
        const S = Math.max(x1 - x0, y1 - y0) * 1.06; // a little margin
        return { x: (x0 + x1) / 2 - S / 2, y: (y0 + y1) / 2 - S / 2, w: S, h: S };
      }
    }

    const S = CouorMap.CLEARING_SPAN;
    return { x: this.clearing.cx - S / 2, y: this.clearing.cy - S / 2, w: S, h: S };
  }

  measureClearing() {
    try {
      const charted = this.sharpSvg.querySelector('#cm-charted path');
      if (!charted) return;
      const bb = charted.getBBox();
      this.clearing = {
        cx: bb.x + bb.width / 2,
        cy: bb.y + bb.height / 2,
        r: Math.max(bb.width, bb.height) / 2,
      };
    } catch (err) {
      /* geometry unavailable — the bake falls back to no clear zone */
    }
  }

  // The drifting weather layer. The map's own fog is baked into the
  // artwork and stays put; this is the motion on top of it — the tileable
  // cloud from the map build, masked by the clearing so the charted
  // region stays open, drifting on CSS transforms alone.
  // Drifting weather, in SCREEN space.
  //
  // This used to be sized to the world (8400px) and scaled with the camera,
  // which made its two drift children `inset: -25%` of that — 12,600 x
  // 12,600px elements carrying `will-change: transform`. That is ~635M
  // pixels of compositor layer each: the browser refused to lay them out
  // (they measured 0x0 and never animated) and the allocation crashed
  // phone tabs outright. Weather is atmosphere on the glass, so it belongs
  // in front of the map at viewport size, where it costs almost nothing.
  // Drifting weather over the charted area only.
  //
  // Two earlier versions were wrong in instructive ways. Spanning the whole
  // world made the drift children 12,600px squares that crashed phone tabs.
  // Spanning the viewport was safe but pointless: barely visible, and it
  // slid across the map instead of belonging to it. This version is
  // anchored to the charted box — it moves WITH the territory, is small
  // enough to be free, and is only rendered where anyone is looking
  // closely. Zoomed out to the whole world it is simply too small to
  // matter, which is the correct outcome on a phone.
  buildWeather() {
    try {
      const assets = this.dataset;
      const view = this.clearingViewBox();
      if (!assets.cloudUrl || !view) return;

      const wrap = document.createElement('div');
      wrap.className = 'couor-map__weather';

      for (const variant of ['a', 'b']) {
        const layer = document.createElement('div');
        layer.className = `couor-map__weather-drift couor-map__weather-drift--${variant}`;
        layer.style.backgroundImage = `url(${assets.cloudUrl})`;
        // Tile size is in MAP units now (the wrapper is sized in map units
        // and scaled by the camera), so the cloud scale stays consistent
        // with the terrain however far you are zoomed in.
        layer.style.backgroundSize = variant === 'a' ? '340px 340px' : '520px 520px';
        wrap.appendChild(layer);
      }
      this.appendChild(wrap);
      this.weatherEl = wrap;
      this.weatherView = view;
      this.applyCamera();
    } catch (err) {
      /* decorative only — never let it break the map */
    }
  }

  // Keep the weather glued to its patch of world (called from applyCamera).
  positionWeather(pC) {
    if (!this.weatherEl || !this.weatherView) return;
    const v = this.weatherView;
    const px = pC.offsetX + (v.x - this.camera.x) * pC.scale;
    const py = pC.offsetY + (v.y - this.camera.y) * pC.scale;
    this.weatherEl.style.width = `${v.w}px`;
    this.weatherEl.style.height = `${v.h}px`;
    this.weatherEl.style.transform = `translate(${px}px, ${py}px) scale(${pC.scale})`;
  }

  // Run the clearing's animation on the visible layer only, and re-point it
  // when the layers swap. Animating the copy underneath is invisible work.
  // The moving decor — waves, rocking boats, the surfacing monster — gets
  // its own small layer.
  //
  // It cannot live in the bitmap tiles: a bitmap is one frozen frame, so
  // baking it put a stopped copy underneath the live one and the two drifted
  // visibly out of phase. It cannot live in the main vector layers either:
  // those are hidden during a zoom (a full-artwork layer re-rasterises far
  // too slowly to scale), which made the decor vanish and pop back.
  //
  // So it becomes a third, deliberately tiny layer: only the animated
  // elements and the ancestors they need for their transforms, windowed to
  // the charted box. Roughly three dozen elements instead of ~2,000, which
  // is cheap enough to scale live — so it is simply always on screen.
  buildLifeLayer(sourceSvg) {
    try {
      const view = this.clearingViewBox();
      if (!view) return;
      const life = sourceSvg.cloneNode(true);

      // Keep each animated element, everything inside it, and its ancestor
      // chain — the ancestors carry the transforms that place it correctly.
      const keep = new Set();
      const animated = life.querySelectorAll(CouorMap.ANIMATED_SELECTOR);
      if (!animated.length) return;
      animated.forEach((el) => {
        keep.add(el);
        el.querySelectorAll('*').forEach((d) => keep.add(d));
        for (let p = el.parentNode; p && p !== life; p = p.parentNode) keep.add(p);
      });

      // Definitions stay whole, wherever they live. Keeping only the first
      // <defs> was not enough: the monster's clip path is declared outside
      // it, so pruning threw it away. An unresolvable `clip-path` renders
      // the element UNCLIPPED — which is why the monster appeared beached
      // instead of cut off at the waterline.
      const DEFINITION_TAGS = new Set([
        'defs', 'clipPath', 'mask', 'filter', 'linearGradient', 'radialGradient',
        'pattern', 'symbol', 'marker', 'style',
      ]);
      Array.from(life.children).forEach(function prune(node) {
        if (DEFINITION_TAGS.has(node.tagName)) return; // keep the whole subtree
        if (!keep.has(node)) {
          node.remove();
          return;
        }
        Array.from(node.children).forEach(prune);
      });

      // Every definition this layer uses must be ITS OWN, under an id no
      // other layer shares. `url(#id)` resolves against the whole document
      // in DOM order, so the monster's clip-path was actually resolving to
      // the FIRST #cm-smclip -- inside a sharp layer. Zooms set the sharp
      // layers display:none, a resource in a hidden subtree is treated as
      // invalid, and an invalid clip reference renders the element
      // UNCLIPPED: the submerged monster surfaced over the continent for
      // exactly the duration of the flight, then vanished when the layers
      // came back. Renaming makes this layer self-sufficient.
      life
        .querySelectorAll('clipPath[id], mask[id], filter[id], linearGradient[id], radialGradient[id], pattern[id]')
        .forEach((def) => {
          const old = def.id;
          const fresh = old + '-life';
          def.id = fresh;
          for (const attr of ['clip-path', 'mask', 'filter', 'fill', 'stroke']) {
            life.querySelectorAll(`[${attr}*="#${old}"]`).forEach((el) => {
              el.setAttribute(attr, el.getAttribute(attr).replace(`#${old})`, `#${fresh})`));
            });
          }
        });

      // Window it to the charted box so the element is ~1250px, not 8400.
      life.setAttribute('viewBox', `${view.x} ${view.y} ${view.w} ${view.h}`);
      life.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      life.removeAttribute('width');
      life.removeAttribute('height');
      life.classList.remove('couor-map__svg', 'couor-map__svg--base', 'couor-map__svg--sharp');
      // The clone carries the sharp layer's inline transform and z-index;
      // wearing them puts this layer at the sharp layer's projection
      // instead of its own. positionLifeLayer owns its style from here.
      life.removeAttribute('style');
      life.classList.add('couor-map__life');
      this.appendChild(life);
      this.lifeEl = life;
      this.lifeView = view;

      // Tighten the window to what the layer actually draws. It was
      // sized to the whole clearing box (~1500 map units) while the
      // monster occupies a couple hundred -- and during a zoom the
      // compositor re-rasterises this layer at every scale checkpoint,
      // paying for all that empty texture each time. Measured content
      // bounds plus rise-room cut that cost by roughly an order of
      // magnitude.
      try {
        const bb = life.getBBox();
        if (bb && bb.width > 0 && bb.height > 0) {
          const pad = Math.max(bb.width, bb.height) * 0.35;
          const tight = {
            x: bb.x - pad,
            y: bb.y - pad,
            w: bb.width + 2 * pad,
            h: bb.height + 2 * pad,
          };
          life.setAttribute('viewBox', `${tight.x} ${tight.y} ${tight.w} ${tight.h}`);
          this.lifeView = tight;
        }
      } catch (err) {
        /* keep the clearing-sized window */
      }

      // Now that it has its own home, remove it from everything else so it
      // is never drawn twice.
      [this.baseSvg, this.sharpSvg].forEach((layer) =>
        layer.querySelectorAll(CouorMap.ANIMATED_SELECTOR).forEach((el) => el.remove())
      );
      this.applyCamera();
    } catch (err) {
      /* decorative — never let it break the map */
    }
  }

  positionLifeLayer(pC) {
    if (!this.lifeEl || !this.lifeView) return;
    const v = this.lifeView;
    // Same tilted projection as the world tile: x is untilted, y and the
    // vertical scale carry the squash. At region zoom tilt is exactly 1.
    const tilt = this.tilt != null ? this.tilt : 1;
    const px = pC.offsetX + (v.x - this.camera.x) * pC.scale;
    const py = pC.offsetY + (v.y * tilt - this.camera.y) * pC.scale;
    this.lifeEl.style.width = `${v.w}px`;
    this.lifeEl.style.height = `${v.h}px`;
    this.lifeEl.style.transform = `translate(${px}px, ${py}px) scale(${pC.scale}, ${pC.scale * tilt})`;
  }

  // The monster script mutates ~20 SVG attributes per frame, and every
  // mutation invalidates its layer -- style, paint and raster, each frame.
  // On a phone that runs concurrently with a flight's compositing and eats
  // exactly the frame budget the pan needs. The CSS animations already
  // hold still during travel (is-travelling); the rAF loop is JS and needs
  // pausing from here. Its timeline is wall-clock, so stopping and
  // restarting just skips ahead -- no state to corrupt.
  setTravelling(on) {
    this.classList.toggle('is-travelling', on);
    // The monster keeps swimming through flights. Pausing it dates from
    // when it was drawn INSIDE the sharp layer, where each of its frames
    // invalidated the whole surface texture mid-flight; on its own small
    // layer that cost is gone. The stop/restart pair was also a live race:
    // a zoom's pre-render promote called startLife() mid-flight, restarting
    // the script the flight had just stopped -- the monster froze at a
    // stray pose, then vanished when the restart reset the cycle, which is
    // exactly the "appears for a second, then flickers out" during a
    // hover zoom. Only the legacy fallback (no dedicated layer) still
    // pauses, because there the texture-invalidation cost is real.
    if (on) {
      // Paused for the flight, resumed at the same phase on arrival. The
      // rAF mutates ~20 attributes a frame, and during a zoom that meant
      // re-rasterising the life layer's texture every frame WHILE its
      // scale was changing -- the single biggest cost of a mobile zoom.
      // Freezing is safe now that the layer's clip is its own (-life ids):
      // a submerged monster stays invisible and a surfaced one stays
      // correctly cut at the waterline, whatever the other layers do.
      if (this._life) {
        this._lifePhase = this._life.phase ? this._life.phase() : 0;
        this._life.stop();
        this._lifeLayer = null;
      }
    } else if (!this.isStatic) {
      this.startLife();
    }
  }

  startLife(layer) {
    // The dedicated life layer always wins: re-pointing at a sharp layer
    // (the pre-lifeEl design, still requested by the promote path) would
    // put the per-frame invalidation back on the big surface texture.
    // Never (re)start mid-flight: the zoom pre-render promote calls this
    // while travelling, and honouring it would undo the flight pause.
    if (this.classList.contains('is-travelling')) return;
    const target = this.lifeEl || layer || this.sharpLayers[this.activeSharp];
    if (!target || typeof window.couorStartMapLife !== 'function') return;
    // Idle-marking is about which SHARP layer is live, not about the life
    // target: with the monster on its own layer, no sharp layer ever
    // equals the target, so comparing against it marked every layer idle
    // and the idle rule silently killed the waves and boat-rock on the
    // active layer too. Marked before the early return so a promotion
    // (which flips activeSharp but keeps the same life target) still
    // refreshes the attributes.
    this.sharpLayers.forEach((l, i) =>
      l.setAttribute('data-couor-idle', i === this.activeSharp ? '0' : '1')
    );
    // Behind a product panel the map is invisible (the panel is near-
    // opaque): the CSS rule already pauses the map's CSS animations
    // there, but this loop is JavaScript and kept writing ~20 attributes
    // at 30Hz for pixels nobody could see -- the whole visit, on the very
    // page ad traffic lands on. The body-class watcher (see render())
    // restarts the cycle, mid-phase, the moment the panel closes.
    if (document.body.classList.contains('couor-product-open')) return;
    if (this._lifeLayer === target) return;
    if (this._life) this._life.stop();
    this._life = window.couorStartMapLife(target, this._lifePhase || 0);
    this._lifeLayer = target;
  }

  // Build the stylesheet that goes INSIDE the rasterised SVG.
  //
  // Only two things belong there: the artwork rules (authored as
  // `couor-map .coast { ... }`) and the palette variables (authored as
  // `couor-map { --land: ... }`). Everything else in the file styles the
  // host element or the UI around it, and must be left out — two earlier
  // attempts each baked a broken map by getting this wrong:
  //   • stripping the `couor-map` prefix wholesale left the palette block
  //     with no selector at all, so every `fill: var(--land)` resolved to
  //     nothing and the chart baked black behind the fog greys;
  //   • rewriting `couor-map` to `svg` everywhere dragged in the host's
  //     own `couor-map { opacity: 0 }` fade-in rule, which hid the entire
  //     artwork and baked a flat background-coloured square.
  // So: take descendant rules (rescoped to `svg `), take custom properties
  // out of the bare host rule, take @font-face, drop the rest.
  static artworkCssFrom(cssText) {
    const src = cssText.replace(/\/\*[\s\S]*?\*\//g, ''); // comments confuse the splitter
    let out = '';

    for (const m of src.matchAll(/@font-face\s*\{[^{}]*\}/g)) out += m[0] + '\n';

    // Drop every at-rule block (media queries, keyframes, font-face) so
    // their inner rules can't leak out of context when flattened.
    const flat = src.replace(/@[\w-]+[^{]*\{(?:[^{}]*\{[^{}]*\})*[^{}]*\}/g, '');

    for (const m of flat.matchAll(/([^{}]+)\{([^{}]*)\}/g)) {
      const selector = m[1].trim();
      const body = m[2].trim();
      if (!selector || !body) continue;

      if (selector === 'couor-map') {
        const vars = body
          .split(';')
          .map((d) => d.trim())
          .filter((d) => d.startsWith('--'))
          .join(';');
        if (vars) out += `svg{${vars}}\n`;
        continue;
      }

      // Descendant rules only — `couor-map .coast`, `couor-map #cm-fog rect`.
      const parts = selector
        .split(',')
        .map((p) => p.trim())
        .filter((p) => /^couor-map\s+/.test(p))
        .map((p) => p.replace(/^couor-map\s+/, 'svg '));
      if (parts.length) out += `${parts.join(',')}{${body}}\n`;
    }
    return out;
  }

  // One prepared clone serves both tiles: cloning and inlining the fog is
  // the expensive part, so it is done once and rasterised at two viewBoxes.
  static blobToDataUri(blob) {
    return new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result);
      fr.onerror = rej;
      fr.readAsDataURL(blob);
    });
  }

  async prepareRasterClone() {
    // The base layer may be a stub (prebaked mode) or already a canvas --
    // the sharp layer always carries the full artwork, so prefer it.
    const source =
      this.sharpSvg && this.sharpSvg.tagName === 'svg' ? this.sharpSvg : this.baseSvg;
    const clone = source.cloneNode(true);
    // The live layer wears the camera's inline transform; serialized into
    // a standalone SVG it would shear the rasterisation.
    clone.removeAttribute('style');
    clone.classList.remove('couor-map__svg', 'couor-map__svg--base', 'couor-map__svg--sharp', 'is-stale', 'is-flying');

    // The falling rim water is animated in the live vector layer, so it must
    // NOT be baked into the tiles as well: a frozen copy underneath a moving
    // one halves the sense of flow, and it is what made the water look
    // stopped rather than absent while the mask was broken. Same rule the
    // creatures follow (see startLife) -- whatever moves is drawn in exactly
    // one place. The rim mist stays baked; only the streaks are lifted.
    clone.querySelectorAll('#cm-fallStreaks').forEach((el) => el.remove());
    // Same rule for the monster: it lives on the life layer, which stays
    // up during flights -- a frozen copy in the tile double-drew beneath
    // it whenever the tile carried the picture.
    clone.querySelectorAll(CouorMap.ANIMATED_SELECTOR).forEach((el) => el.remove());

    // The artwork's styling lives in the page stylesheet, scoped under
    // `couor-map` — a serialised SVG rasterises with NONE of it, and every
    // path falls back to SVG's default black fill. The palette has to
    // travel with the clone or the map bakes black under the fog.
    const cssLink = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).find((l) =>
      (l.getAttribute('href') || '').includes('couor-map.css')
    );
    if (!cssLink) throw new Error('map css not found');
    const cssText = await (await fetch(cssLink.href)).text();
    const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
    style.textContent = CouorMap.artworkCssFrom(cssText);
    clone.insertBefore(style, clone.firstChild);
    // Kept so each tile can restyle before its own rasterisation.
    clone._couorStyleEl = style;
    clone._couorCss = cssText;

    // The live layers may have their dressing hrefs stripped (deferDressing);
    // the TILES must bake complete -- the world tile IS the zoomed-out view,
    // fog and rim included -- so the clone gets them back first. This is
    // where the textures actually download on content pages: at bake time,
    // on idle, behind the product photos.
    clone.querySelectorAll('image[data-couor-defer]').forEach((img) => {
      img.setAttribute('href', img.getAttribute('data-couor-defer'));
      img.removeAttribute('data-couor-defer');
    });

    // External images are blocked when an SVG renders inside an <img>, so
    // inline them. (Creatures already ship as data URIs; the fog does not.)
    //
    // The Accept header matters more than it looks: a bare fetch() sends
    // Accept: */*, and Shopify's CDN answers that with a PNG TRANSCODE of
    // the asset for compatibility -- measured 4,541 KB for the 952 KB
    // rim mist, 759 KB for the 309 KB fog -- downloaded separately from
    // the <image> copies because the cache key differs. Asking for webp
    // like an <img> does gets the real file AND shares its cache entry,
    // so on a warm page these fetches cost no network at all.
    await Promise.all(
      Array.from(clone.querySelectorAll('image')).map(async (img) => {
        const href = img.getAttribute('href');
        if (!href || href.startsWith('data:')) return;
        try {
          // Chromium's exact image-request Accept string, so on the
          // dominant mobile browsers this fetch is a CACHE HIT on the
          // <image> tag's entry (the CDN sends Vary: Accept -- any other
          // string is a second full download; a bare fetch is worse still,
          // a multi-megabyte PNG transcode).
          const resp = await fetch(href, {
            mode: 'cors',
            headers: { Accept: 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8' },
          });
          const blob = await resp.blob();
          img.setAttribute('href', await CouorMap.blobToDataUri(blob));
        } catch (err) {
          img.remove(); // a missing figure beats a failed bitmap
        }
      })
    );
    // Strip the ANIMATED decor from anything we rasterise.
    //
    // A bitmap is a single frozen frame. The waves, the rocking boats and
    // the sea monster keep animating in the live vector layer, so baking
    // them too puts two phases of the same squiggle on screen at once —
    // the "leftover frame from the last location" ghosting. They belong to
    // the vector layer alone; the tiles carry terrain, fog and static
    // marginalia, which align exactly and cannot drift out of phase.
    // One source of truth. This used to be a second, hardcoded copy of the
    // selector: when the boats were removed from ANIMATED_SELECTOR so they
    // could stay in the artwork, this copy kept stripping them from every
    // bake — so the moment the tile started carrying the render on its own,
    // the boats vanished.
    // The tiles DO carry the animated decor, frozen. That is deliberate:
    // during motion the tile is the only thing on screen, so if the waves
    // and boats were missing they would blink out and pop back. A frozen
    // frame for the half second of travel is unnoticeable; an absence is
    // not. Nothing doubles, because the tile and the vector layers are
    // never visible at the same time (see syncTileVisibility).

    clone.style.transform = '';
    return clone;
  }

  // Rasterise the prepared clone at a given view into a square of sizePx.
  async rasteriseClone(clone, view, wPx, hPx) {
    hPx = hPx || wPx;
    clone.setAttribute('viewBox', `${view.x} ${view.y} ${view.w} ${view.h}`);
    clone.setAttribute('width', wPx);
    clone.setAttribute('height', hPx);
    clone.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    const url = URL.createObjectURL(
      new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml' })
    );
    try {
      const img = new Image();
      img.decoding = 'async';
      await new Promise((res, rej) => {
        img.onload = res;
        img.onerror = rej;
        img.src = url;
      });
      // decode() rasterises the SVG off the main thread; without it the
      // first drawImage pays the whole decode synchronously -- on a phone
      // that is a several-hundred-ms stall right when the user first taps.
      if (img.decode) await img.decode().catch(() => {});
      return img;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  // Coastlines are drawn with `vector-effect: non-scaling-stroke`, so in
  // the live vector layer a hairline stays a hairline at every zoom. A
  // rasterised tile cannot do that — its strokes are baked in, so showing
  // the tile magnified renders every coastline that much heavier. That is
  // the "thick lines" on Cuba, the Bahamas and the island chains while
  // travelling.
  //
  // Matching the screen 1:1 in pixels would need a ~47MB texture. Instead,
  // bake the strokes THINNER by exactly the magnification the tile will be
  // shown at, so they land at their true weight on screen. The tile is then
  // resolution-soft rather than heavy-lined, which is far less visible.
  static scaleStrokeWidths(css, factor) {
    if (!(factor > 0) || Math.abs(factor - 1) < 0.02) return css;
    return css.replace(/stroke-width\s*:\s*([\d.]+)(px)?/g, (m, n, px) => {
      const scaled = Math.max(0.05, parseFloat(n) * factor);
      return `stroke-width:${scaled.toFixed(3)}${px || ''}`;
    });
  }

  async buildBitmapBase() {
    if (this._bitmapBaseDone || this.baseSvg.tagName === 'CANVAS') return;
    this._bitmapBaseDone = true;

    // Prebaked path: the tiles were rasterised once at build time
    // (bake_tiles.py in the Website folder) and ship as static WebP
    // assets, so a visitor's phone decodes two images instead of
    // serialising and rasterising the 892KB artwork twice on its main
    // thread. The live bake below survives untouched as the fallback for
    // missing assets or a failed fetch.
    if (this.dataset.tileWorld) {
      try {
        await this.loadPrebakedTiles();
        if (this._hasDeferredDressing) {
          const done = () => this.restoreDressing();
          if (window.requestIdleCallback) requestIdleCallback(done, { timeout: 3000 });
          else setTimeout(done, 400);
        }
        return;
      } catch (err) {
        /* fall through to the on-device bake */
      }
    }

    const clone = await this.prepareRasterClone();
    const mobile = this.isMobile();

    // --- tile 1: the chart itself, over the content box (not the sky) ---
    const CV = CouorMap.CONTENT_VIEW;
    const maxPx = mobile ? 1700 : 2600;
    const wPx = maxPx;
    const hPx = Math.round((maxPx * CV.h) / CV.w);

    const worldImg = await this.rasteriseClone(clone, CV, wPx, hPx);
    const canvas = document.createElement('canvas');
    canvas.width = wPx;
    canvas.height = hPx;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(worldImg, 0, 0, wPx, hPx);
    if (ctx.getImageData(wPx >> 1, Math.round(hPx * 0.25), 1, 1).data[3] === 0) {
      throw new Error('bitmap came out empty');
    }
    canvas.className = 'couor-map__world-tile';
    this.baseSvg.replaceWith(canvas);
    this.baseSvg = canvas;
    this.baseIsWorldTile = true;
    this.worldTileView = CV;
    this.applyCamera();

    // --- tile 2: the charted area, at travel resolution ---
    await this.buildClearingTile(clone);

    // The bake just fetched the dressing textures for the tiles, so they
    // are warm in the HTTP cache -- putting them back into the live layers
    // now costs no network and closes the window where a zoom-out could
    // catch the rim bare.
    if (this._hasDeferredDressing) {
      const done = () => this.restoreDressing();
      if (window.requestIdleCallback) requestIdleCallback(done, { timeout: 3000 });
      else setTimeout(done, 400);
    }
  }

  // The charted square, rasterised densely enough that panning and zooming
  // between menu destinations is always covered by sharp pixels. This is
  // what removes the soft strip that slid in during a pan and the repaint
  // that snapped it back: there is nothing left to repaint.
  async buildClearingTile(preparedClone) {
    if (!this.clearing || this._clearingTileDone) return;
    this._clearingTileDone = true;
    try {
      const clone = preparedClone || (await this.prepareRasterClone());
      const S = CouorMap.CLEARING_SPAN;
      const view = this.clearingViewBox();
      // Enough pixels that the tile is never magnified at the zoom the menu
      // actually travels to (REGION_VIEW_W across the viewport), plus a
      // little headroom, capped so the texture stays sane in GPU memory.
      const rect = this.hostRect();
      const needed = (S / CouorMap.REGION_VIEW_W) * rect.width * Math.min(devicePixelRatio, 2) * 1.15;
      const sizePx = Math.round(Math.min(this.isMobile() ? 1800 : 2800, Math.max(1200, needed)));

      // How magnified this tile will be when the camera is at a region,
      // and therefore how much to thin its strokes so they land true.
      const targetPxPerUnit =
        (rect.width / CouorMap.REGION_VIEW_W) * Math.min(devicePixelRatio, 2);
      const tilePxPerUnit = sizePx / S;
      const strokeScale = Math.min(1, tilePxPerUnit / targetPxPerUnit);
      if (clone._couorStyleEl) {
        clone._couorStyleEl.textContent = CouorMap.scaleStrokeWidths(
          CouorMap.artworkCssFrom(clone._couorCss),
          strokeScale
        );
      }
      this.clearingTileStrokeScale = +strokeScale.toFixed(3);

      const img = await this.rasteriseClone(clone, view, sizePx);
      const tile = document.createElement('canvas');
      tile.width = sizePx;
      tile.height = sizePx;
      const tctx = tile.getContext('2d');
      tctx.drawImage(img, 0, 0, sizePx, sizePx);
      if (tctx.getImageData(sizePx >> 1, sizePx >> 1, 1, 1).data[3] === 0) return;

      // Feather the edges to transparent. This tile is far denser than the
      // world raster beneath it, so where it simply stopped there was a
      // visible rectangle: sharp coastlines and crisp fog inside, 3x-thick
      // magnified strokes and washed grey outside. Fading it out over its
      // outer margin turns that hard boundary into a gradient nobody can
      // pick out, while the world raster keeps carrying fog everywhere.
      const fade = Math.round(sizePx * 0.14);
      tctx.globalCompositeOperation = 'destination-out';
      const edges = [
        [0, 0, sizePx, fade, 0, 0, 0, fade],
        [0, sizePx - fade, sizePx, fade, 0, sizePx, 0, sizePx - fade],
        [0, 0, fade, sizePx, 0, 0, fade, 0],
        [sizePx - fade, 0, fade, sizePx, sizePx, 0, sizePx - fade, 0],
      ];
      for (const [rx, ry, rw, rh, x0, y0, x1, y1] of edges) {
        const g = tctx.createLinearGradient(x0, y0, x1, y1);
        g.addColorStop(0, 'rgba(0,0,0,1)');
        g.addColorStop(1, 'rgba(0,0,0,0)');
        tctx.fillStyle = g;
        tctx.fillRect(rx, ry, rw, rh);
      }
      tctx.globalCompositeOperation = 'source-over';

      tile.className = 'couor-map__clearing-tile';
      this.clearingView = view;
      this.clearingTileEl = tile;
      this.baseSvg.after(tile);
      this.applyCamera();
    } catch (err) {
      /* the world tile alone still works — this is an enhancement */
    }
  }

  // Keep the charted tile glued to its patch of world (from applyCamera).
  positionClearingTile(pC) {
    if (!this.clearingTileEl || !this.clearingView) return;
    const v = this.clearingView;
    // Same TILTED projection as the world tile and the life layer. This
    // tile is what carries the picture during a zoom (the vector layers
    // are hidden), and it was the one layer projected without tilt -- so
    // for the whole flight, while tilt eased from 0.45 to 1, it rendered
    // unsquashed and vertically displaced, failing to register over the
    // world tile beneath. The visible result was the world tile alone,
    // magnified ~7x past its resolution: the "lose all the resolution,
    // terribly fuzzy" zoom. At arrival tilt reaches 1 and it snapped back.
    const tilt = this.tilt != null ? this.tilt : 1;
    const tilePx = this.clearingTileEl.width || 1;
    const sx = (pC.scale * v.w) / tilePx;
    const px = pC.offsetX + (v.x - this.camera.x) * pC.scale;
    const py = pC.offsetY + (v.y * tilt - this.camera.y) * pC.scale;
    this.clearingTileEl.style.width = `${tilePx}px`;
    this.clearingTileEl.style.height = `${tilePx}px`;
    this.clearingTileEl.style.transform = `translate(${px}px, ${py}px) scale(${sx}, ${sx * tilt})`;
  }


  // The world dressing -- the rim mist curtain (952KB), the fog field
  // (267KB) and the fall-streak textures (52KB) -- is only visible zoomed
  // out at the world view. Ad traffic lands zoomed into a region with the
  // panel open, where all of it is off screen; loading ~1.3MB of texture
  // there spends the connection on pixels nobody sees. Stripping the hrefs
  // before the layers enter the DOM stops the browser fetching them;
  // restoreDressing puts them back the moment they could matter. Only on
  // content pages (data-defer-art) -- the homepage lands at the world view
  // where the dressing IS the picture.
  deferDressing(svg) {
    svg.querySelectorAll('#cm-rimMist, #cm-fog image, #cm-fallStreaks image, mask image').forEach((img) => {
      const href = img.getAttribute('href');
      if (!href) return;
      img.setAttribute('data-couor-defer', href);
      img.removeAttribute('href');
    });
    this._hasDeferredDressing = true;
  }

  restoreDressing() {
    if (!this._hasDeferredDressing || this._dressingRestored) return;
    this._dressingRestored = true;
    this.querySelectorAll('image[data-couor-defer]').forEach((img) => {
      img.setAttribute('href', img.getAttribute('data-couor-defer'));
      img.removeAttribute('data-couor-defer');
    });
  }

  // The water falling off the rim is masked by <mask><use href="#cm-rimMist"/></mask>
  // -- the only two <use> elements in the whole artwork, and the only
  // references in it that cannot survive this engine.
  //
  // Everything else in the file is a paint-time url(#id) lookup, which
  // re-resolves whenever the DOM changes. <use> is different: it builds a
  // shadow tree once. We put TWO copies of the artwork in the page (the base
  // layer and its sharp clone), so both carry id="cm-rimMist" -- and the
  // sharp copy's <use>, resolving by document order, bound to the BASE
  // copy's image. buildBitmapBase then replaces the base layer with a
  // canvas, destroying the element the sharp layer's mask was pointing at.
  // The shadow tree goes stale, the mask renders zero alpha, and the streaks
  // are masked out of existence -- silently, because the still copy baked
  // into the world tile looks exactly like water that simply isn't moving.
  //
  // Inlining the referenced image into each mask removes the indirection
  // altogether, so no layer's mask depends on any other layer surviving.
  inlineFallMask(svg) {
    const rim = svg.querySelector('#cm-rimMist');
    if (!rim) return;
    svg.querySelectorAll('mask use').forEach((use) => {
      const href = use.getAttribute('href') || use.getAttribute('xlink:href');
      if (href !== '#cm-rimMist') return;
      const copy = rim.cloneNode(true);
      // The id is dropped deliberately: the mask's own copy must stay
      // invisible to `#cm-rimMist` rules so the visible mist can be tuned
      // without also dimming the alpha that reveals the streaks.
      copy.removeAttribute('id');
      use.replaceWith(copy);
    });

    // The streak group's wrapper carries the mask and the opacity but has
    // no handle of its own; give it one so the stylesheet can reach it.
    const streaks = svg.querySelector('#cm-fallStreaks');
    if (streaks && streaks.parentElement) streaks.parentElement.classList.add('cm-fallwrap');
  }

  // The creature marginalia are referenced by bare filename so the SVG
  // works as a standalone asset; once inlined those relative hrefs would
  // resolve against the page URL, so point them back at the asset folder.
  absolutiseCreatures(svg) {
    const svgUrl = new URL(this.dataset.svgUrl, location.href);
    svgUrl.search = '';
    const dir = svgUrl.href.replace(/[^/]*$/, '');
    svg.querySelectorAll('image').forEach((img) => {
      const href = img.getAttribute('href') || img.getAttribute('xlink:href');
      if (href && !/^(https?:)?\/\//.test(href) && !href.startsWith('data:')) {
        img.setAttribute('href', dir + href);
      }
    });
  }

  placeRegions() {
    for (const region of this.regions) {
      if (Number.isNaN(region.pos[0]) || Number.isNaN(region.pos[1])) continue;
      region.el.classList.add('is-placed');
    }
    this.updateRegionTransforms();
  }

  // The layout box of the map (the transform on the svg never changes
  // this) — the frame all camera math is done against. Cached: reading
  // getBoundingClientRect every animation frame forces layout right after
  // the previous frame's style writes, which is per-frame jank on phones.
  hostRect() {
    if (!this._hostRect) this._hostRect = this.getBoundingClientRect();
    return this._hostRect;
  }

  invalidateHostRect() {
    this._hostRect = null;
  }

  // "slice" scaling: the visible map area covers the container.
  mapScale(rect, vb) {
    const scale = Math.max(rect.width / vb.w, rect.height / vb.h);
    return { scale, offsetX: (rect.width - vb.w * scale) / 2, offsetY: (rect.height - vb.h * scale) / 2 };
  }

  // Express the camera as a compositor transform on the base layer (whose
  // painted view is always the whole world), keep the markers glued to
  // their map positions, and hide the sharp layer the moment the camera
  // leaves the view it was painted at. Nothing here repaints anything.
  applyCamera() {
    if (!this.baseSvg) return;
    // The rim enters the frame somewhere around w~2500; restoring here
    // (idempotent, flag-guarded) means any zoom-out fetches the dressing
    // while the flight is still running.
    if (this._hasDeferredDressing && !this._dressingRestored && this.camera.w > 2500) this.restoreDressing();
    const rect = this.hostRect();
    if (!rect.width || !rect.height) return;
    const pC = this.mapScale(rect, this.camera);
    const pW = this.mapScale(rect, CouorMap.WORLD_VIEW);
    // Every layer is painted untilted, so the squash is applied here, on the
    // vertical axis only: a world y renders at y * tilt.
    const tilt = CouorMap.tiltFor(this.camera.w);
    this.tilt = tilt;
    // The starfield twinkle and the rim water only read at the zoomed-out
    // world view -- at region zoom the rim is off screen and the sky is
    // behind the panel, yet both were still invalidating their layers
    // every frame. Explicitly licensed: they may hold still except when
    // the map is fully zoomed out. toggle() with the same value is free.
    this.classList.toggle('is-zoomed', this.camera.w < 6000);
    const k = pC.scale / pW.scale;
    const bx = pC.offsetX - k * pW.offsetX + pC.scale * (CouorMap.WORLD_VIEW.x - this.camera.x);
    const by = pC.offsetY - k * pW.offsetY + pC.scale * (CouorMap.WORLD_VIEW.y - this.camera.y);

    if (this.baseIsWorldTile) {
      // Placed by world coordinates over the content box. x and y scale
      // independently: y additionally carries the tilt.
      const V = this.worldTileView || CouorMap.CONTENT_VIEW;
      const cw = this.baseSvg.width || 1;
      const ch = this.baseSvg.height || 1;
      const wx = pC.offsetX + (V.x - this.camera.x) * pC.scale;
      const wy = pC.offsetY + (V.y * tilt - this.camera.y) * pC.scale;
      const sx = (pC.scale * V.w) / cw;
      const sy = (pC.scale * tilt * V.h) / ch;
      this.baseSvg.style.width = `${cw}px`;
      this.baseSvg.style.height = `${ch}px`;
      this.baseSvg.style.transform = `translate(${wx}px, ${wy}px) scale(${sx}, ${sy})`;
    } else {
      // Same projection as the sharp layers and the world tile, tilt
      // included. It has to be: this vector base is what carries the
      // picture until buildBitmapBase() swaps the canvas in ~600ms later,
      // and the two must be the same pixels or the swap shows. Leaving the
      // tilt off here drew the world flat-on and full-size at load, then
      // snapped it into the tilted disc when the bitmap arrived.
      const ky = k * tilt;
      const byT = pC.offsetY - ky * pW.offsetY + pC.scale * (CouorMap.WORLD_VIEW.y * tilt - this.camera.y);
      this.baseSvg.style.transform = `translate(${bx}px, ${byT}px) scale(${k}, ${ky})`;
    }

    // The sharp layer rides the same camera against its own painted view,
    // so whenever it has content it is always in the right place — during
    // a flight it appears as a crisp window over the softer base. It is
    // hidden only in the gap between a viewBox write and the confirmed
    // paint (managed by commitCamera / the flight pre-paint).
    // Sharp layers are inset by -margin on every side, so their own box is
    // larger than the host and their origin sits at (-margin, -margin).
    // Both facts have to enter the projection: map the viewBox onto the
    // LAYER's box, then shift by the margin to bring it back into host
    // coordinates.
    const M = this.sharpMargin || 0;
    const layerBox = { width: rect.width + 2 * M, height: rect.height + 2 * M };
    for (let i = 0; i < this.sharpLayers.length; i++) {
      const sp = this.sharpViews[i];
      if (!sp) continue;
      const layer = this.sharpLayers[i];
      const box = this.sharpBox && this.sharpBox[i];
      const thisBox = box ? { width: box.w, height: box.h } : layerBox;
      // Origin of the element relative to the host: the viewport-sized mode
      // is inset by -margin, a surface layer sits at 0,0.
      const ox = box ? 0 : -M;
      const pP = this.mapScale(thisBox, sp);
      const ks = pC.scale / pP.scale;
      // A backing buffer still painted at a far wider view gets magnified
      // out of usefulness -- at region zoom the world-painted spare was
      // projected to a 27,000px box carrying pixels mushier than the
      // clearing tile beneath it. Past 3x magnification it is parked.
      // Never the active layer, and never during a flight, whose
      // suppression flow owns display for the duration.
      if (i !== this.activeSharp && !this._flightSuppressesSharp && !layer.classList.contains('is-flying')) {
        const want = ks > 3 ? 'none' : '';
        if (layer.style.display !== want) layer.style.display = want;
        if (want === 'none') continue;
      }
      const ksy = ks * tilt;
      const sbx = pC.offsetX - ks * pP.offsetX + pC.scale * (sp.x - this.camera.x) - ox;
      const sby = pC.offsetY - ksy * pP.offsetY + pC.scale * (sp.y * tilt - this.camera.y) - ox;
      layer.style.transform = `translate(${sbx}px, ${sby}px) scale(${ks}, ${ksy})`;
      // The freshest painted view sits on top; the previous one stays put
      // beneath it as backing, so the screen is never showing only the
      // low-resolution world base during a move.
      // Above the clearing tile (z-index 1); freshest painted view on top.
      layer.style.zIndex = i === this.activeSharp ? '3' : '2';
    }

    for (const region of this.regions) {
      const [mx, my] = region.pos;
      const px = pC.offsetX + (mx - this.camera.x) * pC.scale;
      const py = pC.offsetY + (my * tilt - this.camera.y) * pC.scale;
      // transform, not left/top: moving a composited marker never
      // invalidates layout, so animation frames stay layout-free.
      region.el.style.transform = `translate(${px}px, ${py}px) translate(-50%, -50%)`;
    }

    this.positionClearingTile(pC);
    this.positionWeather(pC);
    this.positionLifeLayer(pC);

  }

  // One real repaint, on the hidden sharp layer only: write its viewBox,
  // let the browser actually rasterise it (two frames), then fade it in
  // over the always-correct base. The base never repaints at all, so no
  // frame can ever show a stale raster under a fresh transform.
  commitCamera() {
    if (this._commitTimer) {
      clearTimeout(this._commitTimer);
      this._commitTimer = null;
    }
    this.repaintSharpAt({ ...this.camera });
  }

  // Write a new view into the sharp layer while it is hidden, and reveal
  // it only once the browser has demonstrably painted it (two frames).
  // The base layer is untouched, so the screen is never wrong meanwhile.
  // Desktop gets a second sharp buffer, created on first use. Hiding the
  // only sharp layer while it re-rasterised was what dropped the map to
  // the upscaled world base mid-move — with two buffers the outgoing
  // render simply stays on screen until the incoming one is painted.
  ensureSharpBuffer() {
    if (this.sharpLayers.length > 1 || this.isMobile()) return;
    const extra = this.sharpLayers[0].cloneNode(true);
    extra.classList.remove('is-stale');
    this.sharpLayers[0].after(extra);
    this.sharpLayers.push(extra);
    this.sharpViews.push({ ...this.sharpViews[0] });
    this.sharpBox.push(this.sharpBox[0] ? { ...this.sharpBox[0] } : null);
    this.applyLayerBox(extra, this.sharpBox[1]);
    this.sharpFlags.push(true);
  }

  // The sharp layers are drawn into a box larger than the viewport, but an
  // oversized BOX alone changes nothing: `preserveAspectRatio: slice` just
  // scales the same viewBox up to fill it, so the layer still covers only
  // the visible area and its edge still sweeps the screen on a pan. The
  // layer has to PAINT a wider view as well — expanded by exactly the
  // margin, converted from screen pixels into map units — so the extra box
  // holds extra map and the edges sit safely offscreen.
  // A vector layer big enough to hold the ENTIRE charted square, painted
  // once. This is what removes the repaint from lateral travel: the layer
  // is not sized to the viewport and painted at the current camera (which
  // must be re-painted the moment the camera moves), it is sized to the
  // territory. Moving between Home and Bermuda then costs one transform on
  // a surface that is already there — and because it is still live vector,
  // the waves, boats and monster keep animating on it.
  //
  // Returns null when the square cannot be covered at a sensible texture
  // size, in which case the old viewport-sized behaviour is used.
  clearingSurfacePlan() {
    const view = this.clearingViewBox();
    const rect = this.hostRect();
    if (!view || !rect.width || !rect.height) return null;

    // Density that makes the layer exactly 1:1 at the zoom the menu flies
    // to, so it is never magnified there.
    const density = Math.max(rect.width, rect.height) / CouorMap.REGION_VIEW_W;
    const dpr = Math.min(devicePixelRatio || 1, 3);
    const maxDevicePx = this.isMobile() ? 3000 : 3600;
    const cssPx = Math.min(view.w * density, maxDevicePx / dpr);
    // Below this it is softer than the bitmap tile already is, so it would
    // be buying nothing for its memory.
    if (cssPx / view.w < density * 0.8) return null;
    return { view, cssPx: Math.round(cssPx) };
  }

  paintedViewFor(view) {
    const M = this.sharpMargin || 0;
    if (!M) return { ...view };
    const rect = this.hostRect();
    if (!rect.width || !rect.height) return { ...view };
    const pad = M / this.mapScale(rect, view).scale;
    return { x: view.x - pad, y: view.y - pad, w: view.w + 2 * pad, h: view.h + 2 * pad };
  }

  // Choose what a layer should be painted at for a given camera view.
  // Inside the charted square we paint the WHOLE square once, so later
  // moves within it need no repaint at all.
  paintPlanFor(view) {
    const plan = this.clearingSurfacePlan();
    if (plan) {
      const v = plan.view;
      const rect = this.hostRect();
      const pC = this.mapScale(rect, view);
      const visW = rect.width / pC.scale;
      const visH = rect.height / pC.scale;
      const cx = view.x + view.w / 2;
      const cy = view.y + view.h / 2;
      const inside =
        cx - visW / 2 >= v.x && cx + visW / 2 <= v.x + v.w &&
        cy - visH / 2 >= v.y && cy + visH / 2 <= v.y + v.h;
      if (inside) return { view: { ...v }, box: { w: plan.cssPx, h: plan.cssPx } };
    }
    return { view: this.paintedViewFor(view), box: null };
  }

  applyLayerBox(layer, box) {
    if (box) {
      layer.style.left = '0px';
      layer.style.top = '0px';
      layer.style.width = `${box.w}px`;
      layer.style.height = `${box.h}px`;
    } else {
      layer.style.left = '';
      layer.style.top = '';
      layer.style.width = '';
      layer.style.height = '';
    }
  }

  repaintSharpAt(view, onReady) {
    // Already painted at exactly what this view needs? Then there is
    // nothing to repaint. This is what makes travel inside the charted
    // square free: every destination in it resolves to the same surface
    // plan, so the second and every subsequent move is pure camera
    // transform over a layer that is already there and already live.
    const planned = this.paintPlanFor(view);
    const cur = this.sharpViews[this.activeSharp];
    const curBox = this.sharpBox && this.sharpBox[this.activeSharp];
    const sameView =
      cur && cur.x === planned.view.x && cur.y === planned.view.y && cur.w === planned.view.w;
    const sameBox = planned.box
      ? !!curBox && curBox.w === planned.box.w
      : !curBox;
    if (sameView && sameBox && this.sharpFlags[this.activeSharp]) {
      this.sharpPainted = view;
      this._sharpReady = true;
      this.applyCamera();
      if (onReady) onReady();
      return;
    }

    this.ensureSharpBuffer();

    if (this.sharpLayers.length > 1) {
      const idx = 1 - this.activeSharp;
      const layer = this.sharpLayers[idx];
      const plan = this.paintPlanFor(view);
      const painted = plan.view;
      this.sharpViews[idx] = painted;
      this.sharpBox[idx] = plan.box;
      this.applyLayerBox(layer, plan.box);
      this.sharpFlags[idx] = false;
      layer.setAttribute('viewBox', `${painted.x} ${painted.y} ${painted.w} ${painted.h}`);
      this.applyCamera(); // positions it correctly while it sits underneath

      const promote = () => {
        // Only if no newer view superseded this one while it painted.
        if (this.sharpViews[idx] !== painted) return;
        this.sharpFlags[idx] = true;
        this.activeSharp = idx;
        this.sharpPainted = view;
        this.startLife(this.sharpLayers[idx]);
        this._sharpReady = true;
        this.applyCamera();
        if (onReady) onReady();
      };
      if (document.hidden) setTimeout(promote, 60);
      else requestAnimationFrame(() => requestAnimationFrame(promote));
      return;
    }

    // Single buffer (mobile): the bitmap base is a good enough stand-in
    // for the brief hidden window, and a second full SVG copy is memory
    // this path deliberately does not spend.
    const planSingle = this.paintPlanFor(view);
    const paintedSingle = planSingle.view;
    this.sharpBox[0] = planSingle.box;
    this.applyLayerBox(this.sharpSvg, planSingle.box);
    this.sharpPainted = view;
    this._sharpReady = false;
    this.sharpSvg.classList.add('is-stale');
    this.sharpSvg.setAttribute(
      'viewBox',
      `${paintedSingle.x} ${paintedSingle.y} ${paintedSingle.w} ${paintedSingle.h}`
    );
    this.sharpViews[0] = paintedSingle;
    this.applyCamera();

    const reveal = () => {
      // Only if no newer view superseded this one while it painted.
      const sp = this.sharpPainted;
      if (sp.x === view.x && sp.y === view.y && sp.w === view.w) {
        this._sharpReady = true;
        this.applyCamera();
        this.sharpSvg.classList.remove('is-stale');
        if (onReady) onReady();
      }
    };
    if (document.hidden) {
      setTimeout(reveal, 60);
    } else {
      requestAnimationFrame(() => requestAnimationFrame(reveal));
    }
  }

  scheduleCommit(ms = 140) {
    if (this._commitTimer) clearTimeout(this._commitTimer);
    this._commitTimer = setTimeout(() => {
      this._commitTimer = null;
      this.commitCamera();
    }, ms);
  }

  // Camera moved by an interaction (not a fly): show it via the base
  // transform now, re-sharpen shortly after the gesture pauses. Never
  // mid-gesture — a repaint can't glitch the view any more (it happens
  // on the hidden layer), it would just burn main-thread time.
  interactTo(vb) {
    this.camera = vb;
    this.applyCamera();
    this.scheduleCommit();
  }

  updateRegionTransforms() {
    this.applyCamera();
  }

  // Kept for external callers (bootstrap restore): jump-cut the camera.
  setViewBox(vb) {
    this.camera = { ...vb };
    this.commitCamera();
  }

  get currentViewBox() {
    return this.camera;
  }

  set currentViewBox(vb) {
    if (vb) this.camera = vb;
  }

  // On mobile the map is menu-only: no free drag or pinch/wheel zoom.
  isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  bindInteraction() {
    let dragging = false;
    let last = null;

    const toMapDelta = (dxPx, dyPx) => {
      const { scale } = this.mapScale(this.hostRect(), this.camera);
      return [-dxPx / scale, -dyPx / scale];
    };

    // A short history of recent samples, so release velocity reflects the
    // actual flick rather than one noisy final pointermove event.
    let track = [];
    const trackWindow = 80; // ms

    const stopSpin = () => {
      if (this._spinFrame) cancelAnimationFrame(this._spinFrame);
      this._spinFrame = null;
    };

    // If the viewer grabs the map mid-flight, the tween never reaches its
    // end — put the vector layers back so they can't stay hidden.
    const clearFlightSuppression = () => {
      clearTimeout(this._restoreTimer);
      this.setTravelling(false);
      if (!this._flightSuppressesSharp) return;
      this._flightSuppressesSharp = false;
      this.sharpLayers.forEach((l) => {
        l.classList.remove('is-flying');
        l.style.display = '';
      });
    };
    this._clearFlightSuppression = clearFlightSuppression;

    this.svgEl.addEventListener('pointerdown', (e) => {
      if (this.isMobile()) return;
      dragging = true;
      stopSpin();
      clearFlightSuppression();
      // Also abandons a take-off still held for the tile bake.
      this._flightToken = (this._flightToken || 0) + 1;
      if (this._flyFrame) cancelAnimationFrame(this._flyFrame);
      last = [e.clientX, e.clientY];
      track = [[e.clientX, e.clientY, performance.now()]];
      this.svgEl.setPointerCapture(e.pointerId);
    });

    this.svgEl.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const [dx, dy] = [e.clientX - last[0], e.clientY - last[1]];
      last = [e.clientX, e.clientY];
      const [mdx, mdy] = toMapDelta(dx, dy);
      this.interactTo({ ...this.camera, x: this.camera.x + mdx, y: this.camera.y + mdy });

      const now = performance.now();
      track.push([e.clientX, e.clientY, now]);
      track = track.filter((s) => now - s[2] <= trackWindow);
    });

    ['pointerup', 'pointercancel', 'pointerleave'].forEach((evt) =>
      this.svgEl.addEventListener(evt, () => {
        if (!dragging) return;
        dragging = false;

        if (track.length >= 2) {
          const [x0, y0, t0] = track[0];
          const [x1, y1, t1] = track[track.length - 1];
          const dt = t1 - t0;
          if (dt > 0) {
            const [mdx, mdy] = toMapDelta(x1 - x0, y1 - y0);
            this.spinVelocity = [mdx / dt, mdy / dt];
            this.startSpin();
            return;
          }
        }
        this.commitCamera();
      })
    );

    this.svgEl.addEventListener(
      'wheel',
      (e) => {
        if (this.isMobile()) return;
        e.preventDefault();
        stopSpin();
        const vb = this.camera;
        const factor = e.deltaY > 0 ? 1.12 : 0.89;
        const newW = Math.min(CouorMap.MAX_VIEW_W, Math.max(CouorMap.MIN_VIEW_W, vb.w * factor));
        const newH = newW * (vb.h / vb.w);
        const rect = this.hostRect();
        const { scale, offsetX, offsetY } = this.mapScale(rect, vb);
        const cx = vb.x + (e.clientX - rect.left - offsetX) / scale;
        const cy = vb.y + (e.clientY - rect.top - offsetY) / scale;
        const fx = (cx - vb.x) / vb.w;
        const fy = (cy - vb.y) / vb.h;
        this.interactTo({ x: cx - fx * newW, y: cy - fy * newH, w: newW, h: newH });
      },
      { passive: false }
    );

    window.addEventListener('resize', () => {
      this.invalidateHostRect();
      this.applyCamera();
    });

    const scope = this.closest('.couor-map-section__viewport') || this.parentElement;
    const resetBtn = scope.querySelector('[data-map-reset]');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        this.deselectAll();
        this.animateViewBox({ ...this.viewBox }, 1200);
      });
    }
  }

  bindRegionEvents() {
    document.addEventListener('couor:fly-to', (e) => {
      const region = this.regions.find((r) => r.anchor === e.detail.anchor);
      if (region) this.flyTo(region, { immediate: !!e.detail.immediate });
    });

    document.addEventListener('couor:deselect', () => this.deselectAll());

    document.addEventListener('couor:zoom-world', () => {
      this.deselectAll();
      this.animateViewBox({ ...this.viewBox }, 1200);
    });

    if (location.hash) {
      const anchor = location.hash.replace(/^#region-/, '');
      const region = this.regions.find((r) => r.anchor === anchor);
      if (region) this.flyTo(region, { immediate: true });
    }
  }

  // Targets are computed in tilted space, because that is where the camera
  // lives. At region zoom the tilt is exactly 1, so the framing the menu
  // flies to is unchanged from before the space scene existed.
  flyTo(region, { immediate = false } = {}) {
    region.el.classList.add('is-revealed');
    this.setActiveLocation(region.name);
    const [mx, myWorld] = region.pos;
    const my = myWorld * CouorMap.tiltFor(CouorMap.REGION_VIEW_W);
    const w = CouorMap.REGION_VIEW_W;
    const h = w;
    let y = my - h / 2;
    const rect = this.hostRect();
    if (rect.width && rect.height) {
      const scale = Math.max(rect.width / w, rect.height / h);
      const visibleH = rect.height / scale;
      const pinFraction = this.isMobile()
        ? CouorMap.PIN_TOP_FRACTION_MOBILE
        : CouorMap.PIN_TOP_FRACTION_DESKTOP;
      y = my - h / 2 + visibleH * (0.5 - pinFraction);
    }
    const target = { x: mx - w / 2, y, w, h };
    if (immediate) {
      this.setViewBox(target);
      this.announceArrival();
      return;
    }
    this.animateViewBox(target, CouorMap.flyMs());
  }

  setActiveLocation(name) {
    const label = document.querySelector('[data-active-location]');
    const menu = document.querySelector('.couor-map__menu');
    if (menu) menu.classList.toggle('is-active', !!name);
    if (!label) return;
    label.textContent = name || '';
    label.hidden = !name;
  }

  deselectAll() {
    for (const region of this.regions) region.el.classList.remove('is-revealed');
    this.setActiveLocation('');
  }

  // The menu opens its panel on this rather than a stopwatch, so the
  // reveal always matches the actual landing regardless of device speed.
  announceArrival() {
    document.dispatchEvent(new CustomEvent('couor:flight-arrived'));
  }

  animateViewBox(target, duration = 900, _tileHoldSpent = false) {
    // Nothing to animate for someone who isn't looking (and rAF wouldn't
    // fire anyway) — just arrive.
    if (document.hidden) {
      this.camera = { ...target };
      this.commitCamera();
      this.setTravelling(false);
      this.announceArrival();
      return;
    }

    // A zoom flight before the world tile has baked runs on the VECTOR
    // base — ~2,000 paths re-rasterised at every scale change, the
    // measured 20fps case — and that is exactly what a fresh-load click
    // hit: the tile image is often still downloading when an eager
    // visitor picks a destination. Hold the take-off until the bitmap
    // lands (checked every 90ms, up to 1.2s; the departure spin covers
    // the beat), then fly on the GPU. Arrival stays event-driven, so the
    // menu choreography simply waits with us. Superseded holds (a newer
    // flight, or the viewer grabbing the map) abandon quietly.
    const holdRatio = Math.max(target.w / this.camera.w, this.camera.w / target.w);
    this._flightToken = (this._flightToken || 0) + 1;
    if (!_tileHoldSpent && !this.baseIsWorldTile && this.dataset.tileWorld && holdRatio > 1.15) {
      const token = this._flightToken;
      const deadline = performance.now() + 1200;
      const tryLaunch = () => {
        if (token !== this._flightToken) return;
        if (this.baseIsWorldTile || performance.now() >= deadline) {
          this.animateViewBox(target, duration, true);
          return;
        }
        setTimeout(tryLaunch, 90);
      };
      setTimeout(tryLaunch, 90);
      return;
    }


    const start = { ...this.camera };
    const startTime = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic — a spin settling, not a snap

    if (this._flyFrame) cancelAnimationFrame(this._flyFrame);
    if (this._spinFrame) cancelAnimationFrame(this._spinFrame);
    this._spinFrame = null;
    // A new journey supersedes any repaint or reveal the previous one was
    // waiting to do.
    clearTimeout(this._panCommitTimer);
    clearTimeout(this._restoreTimer);
    clearTimeout(this._tileWithdrawTimer);
    if (this._commitTimer) {
      clearTimeout(this._commitTimer);
      this._commitTimer = null;
    }

    // A vector layer re-rasterises ~2,000 paths whenever its scale drifts,
    // and that is the entire remaining source of dropped frames in a zoom
    // (measured: p95 43ms with the sharp layers up, 8.8ms with them down,
    // and will-change makes no difference — this is raster, not layout).
    // The bitmap base does not re-raster at any scale, so during a flight
    // that actually CHANGES ZOOM the vector layers stand down and the
    // bitmap carries the movement; they come back the moment it lands.
    //
    // Pans between regions run at constant zoom, never rescale the vector
    // layers, and so stay fully crisp throughout — no suppression there.
    // Quiet the decor for the duration of the journey.
    //
    // Nothing repaints during a lateral move — the surface layer is already
    // painted and only translates — but the waves and boats animating INSIDE
    // it keep invalidating it, so the compositor re-rasterises mid-flight and,
    // under time pressure, does it at reduced quality. That is the softness
    // during travel: not a repaint, a downgraded raster. Pausing them lets
    // the compositor simply move the sharp raster it already has. Travel is
    // ~550ms on a phone, so the pause is imperceptible; the map is crisp
    // instead, which is not.
    this.setTravelling(true);

    const zoomRatio = Math.max(target.w / start.w, start.w / target.w);
    // Suppress the vector layer only when the zoom actually CHANGES, where
    // rescaling it would re-rasterise ~2,000 paths per frame.
    //
    // Phones used to suppress on every flight, from when the tile carried
    // the render and the vector layer was redundant. That is no longer
    // true: the surface layer IS the render now, so hiding it on a lateral
    // move fell all the way back to the world bitmap — a coarse, decor-free
    // copy of the map. A lateral move rescales nothing, so the surface
    // layer can simply translate, already painted and still sharp.
    this._flightSuppressesSharp = zoomRatio > 1.15;

    // A lateral hop between two neighbouring regions is a much smaller
    // journey than a world-to-region zoom, and easeOutCubic spends its
    // last 40% covering ~4% of the distance — over 900ms that reads as
    // crawling rather than settling. Scale the pan with how far it
    // actually travels, and keep it brisk.
    if (!this._flightSuppressesSharp) {
      const dist = Math.hypot(target.x - start.x, target.y - start.y);
      const travel = Math.min(1, dist / Math.max(1, start.w));
      duration = Math.round(Math.min(620, 300 + travel * 520));
    }
    if (this._flightSuppressesSharp) {
      // Driven inline rather than via a stylesheet class: this must be
      // guaranteed to take effect, and it is the difference between a
      // 20fps zoom and a 120fps one.
      this.sharpLayers.forEach((l) => {
        l.classList.add('is-flying');
        l.style.display = 'none';
      });
      // With the vector layers down, the tile has to be the thing on
      // screen — otherwise the only layer left is the world bitmap.
      this.syncTileVisibility();
    }

    // Pre-render the destination ONLY for a zoom, where the layers are
    // hidden and the raster is invisible.
    //
    // A pan must not do this. Its layers stay on screen, so re-viewBoxing
    // one is a ~2,000-path rasterisation on the main thread at the exact
    // moment the pointer moves — which is what made lateral travel feel
    // sticky. A pan doesn't need it either: the destination is at the same
    // zoom, so the crisp layer already on screen is simply translated, it
    // stays sharp the whole way, and the bitmap base covers the strip the
    // translation uncovers. The repaint happens at arrival instead (the
    // end-of-tween commitCamera below), while the map is stationary.
    if (this._flightSuppressesSharp) {
      const sp = this.sharpPainted;
      if (!(this._sharpReady && sp.x === target.x && sp.y === target.y && sp.w === target.w)) {
        this.repaintSharpAt({ ...target });
      }
    }

    const step = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      const k = ease(t);
      this.camera = {
        x: start.x + (target.x - start.x) * k,
        y: start.y + (target.y - start.y) * k,
        w: start.w + (target.w - start.w) * k,
        h: start.h + (target.h - start.h) * k,
      };
      this.applyCamera();
      if (t < 1) {
        this._flyFrame = requestAnimationFrame(step);
      } else {
        this._flyFrame = null;
        // A zoom pre-rendered its destination at take-off, so landing is
        // just the sharp layer reaching identity and there is nothing to
        // repaint. A pan deliberately skipped that, so it repaints here —
        // but on IDLE, not immediately: the raster is ~150ms of main
        // thread, and firing it the instant travel stops puts a stall
        // right at the end of the gesture, which reads as the movement
        // sticking as it settles. Deferred, it slots into a gap after the
        // interaction has visibly finished. repaintSharpAt discards it if
        // another flight supersedes it first.
        const spEnd = this.sharpPainted;
        if (!(spEnd.x === target.x && spEnd.y === target.y && spEnd.w === target.w)) {
          // Debounced, not just deferred. Sweeping the pointer across the
          // menu fires a flight per item; if each one repainted on arrival
          // the sweep would stutter once per item. Each new flight cancels
          // the pending repaint, so a sweep costs zero rasters and the map
          // refines exactly once, after the pointer has actually settled.
          clearTimeout(this._panCommitTimer);
          this._panCommitTimer = setTimeout(() => {
            // Only if the camera is still where this flight left it.
            if (this.camera.x === target.x && this.camera.y === target.y && this.camera.w === target.w) {
              // Inside the charted tile at a zoom it already resolves, the
              // tile alone is the picture — skip the raster completely.
              if (!this.settleOnTile()) this.commitCamera();
            }
          }, 350);
        }
        if (this._flightSuppressesSharp) {
          // Revealing a display:none layer rasterises it, and these layers
          // are deliberately larger than the viewport — that is real
          // main-thread work. It used to happen at 62% of the flight, which
          // is precisely the stutter on the final approach. The viewBox is
          // already correct (pre-set at take-off), so simply showing them
          // later costs one raster and no reflow of the journey.
          //
          // Deferred until after the motion has visibly stopped, and
          // debounced so sweeping the menu never pays for it mid-gesture.
          clearTimeout(this._restoreTimer);
          this._restoreTimer = setTimeout(() => {
            this._flightSuppressesSharp = false;
            this.sharpLayers.forEach((l) => l.classList.remove('is-flying'));
            // Revealed in two steps: display'' at opacity 0 first, so the
            // ~2,000-path raster commits while the layer is still
            // invisible and the tile keeps carrying the picture; the
            // opacity flip and the tile's withdrawal happen together two
            // frames later, after that raster has landed. Doing it in one
            // step tied the tile's disappearance to the raster's commit,
            // and any hiccup there showed the coarse world tile bare.
            this.sharpLayers.forEach((l) => {
              l.style.opacity = '0';
              l.style.display = '';
            });
            requestAnimationFrame(() =>
              requestAnimationFrame(() => {
                this.sharpLayers.forEach((l) => {
                  l.style.opacity = '';
                });
                // The tile holds its post a beat longer. Withdrawing it in
                // this same frame assumed the sharp raster had COMMITTED
                // during the two opacity-0 frames above -- but a phone's
                // first raster of ~2,000 paths lands frames later
                // (opacity-0 content rasterises lazily), and the gap
                // showed the bare 1700px world tile at region zoom: the
                // "flash of poor resolution" on every menu-item journey.
                // With the tile still beneath, the sharp layer simply
                // appears over it whenever its raster lands, and this
                // withdrawal, 350ms on, repaints nothing the eye can see.
                // Costs no raster and no bandwidth -- the tile was already
                // painted; it merely stays composited a third of a second
                // longer.
                clearTimeout(this._tileWithdrawTimer);
                this._tileWithdrawTimer = setTimeout(() => this.syncTileVisibility(), 350);
              })
            );
          }, 260);
        }
        this.setTravelling(false);
        this.announceArrival();
      }
    };
    this._flyFrame = requestAnimationFrame(step);
  }

  startSpin() {
    const friction = 0.94; // per-frame decay — a globe settling, not a hard stop
    const minSpeed = 0.02; // map-units/ms below which we just stop
    let last = performance.now();

    const step = (now) => {
      const dt = now - last;
      last = now;
      let [vx, vy] = this.spinVelocity;
      this.camera = { ...this.camera, x: this.camera.x + vx * dt, y: this.camera.y + vy * dt };
      this.applyCamera();

      vx *= friction;
      vy *= friction;
      this.spinVelocity = [vx, vy];

      if (Math.hypot(vx, vy) > minSpeed) {
        this._spinFrame = requestAnimationFrame(step);
      } else {
        this._spinFrame = null;
        this.commitCamera();
      }
    };
    this._spinFrame = requestAnimationFrame(step);
  }
}

customElements.define('couor-map', CouorMap);

// A top-level `class` in a classic script binds in the global lexical scope,
// not on window — couor-map-menu.js reads flyMs off this, so expose it.
window.CouorMap = CouorMap;
